import {
  Activity,
  Baby,
  Database,
  FileText,
  HardHat,
  HeartOff,
  HeartPulse,
  LayoutDashboard,
  MessageSquareWarning,
  MousePointerClick,
  MoveRight,
  Package,
  ScrollText,
  Settings,
  Shield,
  ShoppingBag,
  Store,
  Users,
  UserCheck,
  UsersRound,
  Vote,
  Wheat,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Role } from '@tirta/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Roles that may see this menu item. Empty = visible to every role.
   * `SUPER_ADMIN` always sees every item regardless of this list.
   *
   * The dashboard is split per role so that different roles never share the
   * same menu surface (and therefore never "collide").
   */
  roles: Role[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Staff roles share the operational overview dashboard (tailored by the data
 * each role is allowed to read). Seller and citizen roles get their own
 * dedicated landing pages instead — see `DashboardHome`.
 */
export const STAFF_ROLES: Role[] = [
  'SUPER_ADMIN',
  'ADMIN_DESA',
  'OPERATOR_DESA',
  'ADMIN_DATA',
  'PETUGAS_KEAMANAN',
  'KADER_KESEHATAN',
  'PENGELOLA_KOPERASI',
  'AUDITOR',
];

export const dashboardNav: NavGroup[] = [
  {
    label: 'Ringkasan',
    items: [
      // Sellers do not see "Dasbor": their workspace item is their dashboard.
      { to: '/app', label: 'Dasbor', icon: LayoutDashboard, roles: [...STAFF_ROLES, 'WARGA'] },
    ],
  },
  {
    label: 'Layanan & Warga',
    items: [
      {
        to: '/app/layanan',
        label: 'Tirta Layanan',
        icon: FileText,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'WARGA'],
      },
      {
        to: '/app/warga',
        label: 'Tirta Warga',
        icon: MessageSquareWarning,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA'],
      },
    ],
  },
  {
    label: 'Data & Usaha',
    items: [
      {
        to: '/app/toko',
        label: 'Toko Saya',
        icon: ShoppingBag,
        roles: ['PELAKU_UMKM'],
      },
      {
        to: '/app/penjual',
        label: 'Pendaftaran Penjual',
        icon: UserCheck,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'PENGELOLA_KOPERASI'],
      },
      {
        to: '/app/padi',
        label: 'Pasar Padi',
        icon: Wheat,
        roles: [
          'SUPER_ADMIN',
          'ADMIN_DESA',
          'OPERATOR_DESA',
          'PENGELOLA_KOPERASI',
          'PELAKU_PADI',
          'WARGA',
        ],
      },
      {
        to: '/app/pupuk',
        label: 'Pupuk',
        icon: Package,
        roles: [
          'SUPER_ADMIN',
          'ADMIN_DESA',
          'OPERATOR_DESA',
          'ADMIN_DATA',
          'PENGELOLA_KOPERASI',
          'PELAKU_PADI',
        ],
      },
      {
        to: '/app/data',
        label: 'Tirta Data',
        icon: Database,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'ADMIN_DATA', 'AUDITOR'],
      },
      {
        to: '/app/jiwa',
        label: 'Data Jiwa',
        icon: UsersRound,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'ADMIN_DATA', 'AUDITOR'],
      },
      {
        to: '/app/pemilih',
        label: 'Data Pemilih',
        icon: Vote,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'ADMIN_DATA', 'AUDITOR'],
      },
      {
        to: '/app/kematian',
        label: 'Data Kematian',
        icon: HeartOff,
        roles: [
          'SUPER_ADMIN',
          'ADMIN_DESA',
          'OPERATOR_DESA',
          'ADMIN_DATA',
          'KADER_KESEHATAN',
          'AUDITOR',
        ],
      },
      {
        to: '/app/perpindahan',
        label: 'Data Perpindahan',
        icon: MoveRight,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'ADMIN_DATA', 'AUDITOR'],
      },
      {
        to: '/app/kelahiran',
        label: 'Data Kelahiran',
        icon: Baby,
        roles: [
          'SUPER_ADMIN',
          'ADMIN_DESA',
          'OPERATOR_DESA',
          'ADMIN_DATA',
          'KADER_KESEHATAN',
          'AUDITOR',
        ],
      },
      {
        to: '/app/niaga',
        label: 'Tirta Niaga',
        icon: Store,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'PENGELOLA_KOPERASI'],
      },
    ],
  },
  {
    label: 'Infrastruktur & Keamanan',
    items: [
      {
        to: '/app/infrastruktur',
        label: 'Tirta Infrastruktur',
        icon: HardHat,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'ADMIN_DATA'],
      },
      {
        to: '/app/aman',
        label: 'Tirta Aman',
        icon: Shield,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'PETUGAS_KEAMANAN'],
      },
    ],
  },
  {
    label: 'Kesehatan',
    items: [
      {
        to: '/app/sehat',
        label: 'Tirta Sehat',
        icon: HeartPulse,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'KADER_KESEHATAN'],
      },
    ],
  },
  {
    label: 'Administrasi',
    items: [
      { to: '/app/notifikasi', label: 'Notifikasi', icon: Activity, roles: [] },
      {
        to: '/app/pengguna',
        label: 'Pengguna',
        icon: Users,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'AUDITOR'],
      },
      {
        to: '/app/audit',
        label: 'Audit Log',
        icon: ScrollText,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'ADMIN_DATA', 'AUDITOR'],
      },
      {
        to: '/app/akses',
        label: 'Log Akses',
        icon: MousePointerClick,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'ADMIN_DATA', 'AUDITOR'],
      },
      {
        to: '/app/pengaturan',
        label: 'Pengaturan',
        icon: Settings,
        roles: ['SUPER_ADMIN', 'ADMIN_DESA', 'OPERATOR_DESA', 'ADMIN_DATA'],
      },
    ],
  },
];
